import React, {
  useContext, useEffect, useMemo, useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { HeaderFooter, Image } from '@telus/koodo-ui-kit'

import {
  APPLICATION_STATE,
  ApplicationContext,
  ModifyApplicationContext
} from '@apps/contexts/ApplicationContext'
import { changeSelectedLanguage, setProvinceId } from '@apps/redux/features/CommonSlice'
import { getById, getImgByTitle } from '@apps/utils/contentful'
import { LANGUAGES } from '@apps/utils/constants'
import { changeLang, changeRegion } from '@apps/utils/locale'

const SiteHeader = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const pageContent = useSelector((state) => state.common?.content?.pageContent)
  const setAppState = useContext(ModifyApplicationContext)
  const appState = useContext(ApplicationContext)
  const previousRegionRef = useRef(null)

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage,
    [APPLICATION_STATE.CURRENT_REGION]: currentRegion,
    [APPLICATION_STATE.PROVINCES]: provinceList
  } = appState

  const resolvedLanguage = String(currentLanguage || 'en').toLowerCase()
  const resolvedRegion = String(currentRegion || 'on').toLowerCase()

  const headerLocale = {
    lang: resolvedLanguage,
    prov: resolvedRegion
  }

  const commonElements = useMemo(() => {
    if (!pageContent || !resolvedLanguage) return {}
    const page = getById(pageContent, 'commonElements')
    return page?.[resolvedLanguage] || {}
  }, [pageContent, resolvedLanguage])

  const koodoLogo = useMemo(() => {
    const koodoLog = getImgByTitle(commonElements, 'koodo-log')
    return koodoLog?.url ? koodoLog : getImgByTitle(commonElements, 'logo') || {}
  }, [commonElements])

  const localeOptions = useMemo(() => {
    const provinces = Array.isArray(provinceList) && provinceList.length > 0
      ? provinceList
        .map((province) => {
          const label = province?.label

          return {
            code: String(province?.shortName || '').toLowerCase(),
            label: typeof label === 'string'
              ? label
              : label?.[resolvedLanguage] || label?.en || province?.shortName || ''
          }
        })
        .filter((province) => province.code)
      : undefined

    return {
      languages: LANGUAGES.map((language) => ({
        code: language.value,
        label: language.label
      })),
      provinces
    }
  }, [provinceList, resolvedLanguage])

  useEffect(() => {
    if (!provinceList || provinceList.length === 0 || !currentRegion) return

    const currentProvinceDetails = provinceList.find(
      (province) => String(province?.shortName).toLowerCase() === String(currentRegion).toLowerCase()
    )

    dispatch(setProvinceId(currentProvinceDetails?.value))

    if (previousRegionRef.current && previousRegionRef.current !== currentRegion) {
      window.dispatchEvent(new CustomEvent('provinceChanged', {
        detail: {
          newProvince: currentRegion,
          oldProvince: previousRegionRef.current
        }
      }))
    }

    previousRegionRef.current = currentRegion
  }, [provinceList, currentRegion, dispatch])

  const updateLanguageAppState = (newLanguage) => {
    setAppState({
      ...appState,
      [APPLICATION_STATE.CURRENT_LANGUAGE]: newLanguage,
      [APPLICATION_STATE.SHOW_SELECT_LANGUAGE]: false
    })
    dispatch(changeSelectedLanguage(newLanguage))
  }

  const handleLanguageChange = (newLanguage) => {
    changeLang(newLanguage, location, history, updateLanguageAppState)
  }

  const handleProvinceChange = (newProvince) => {
    changeRegion(String(newProvince).toUpperCase(), location, history, setAppState, appState)
  }

  const logoNode = koodoLogo?.url ? (
    <div className="flex flex-col items-start">
      <Image
        src={koodoLogo.url}
        alt={koodoLogo.altText || 'Koodo'}
        width={144}
        className="w-28 md:w-36 h-auto"
      />
    </div>
  ) : undefined

  return (
    <div className="kpre-site-header">
      <HeaderFooter
        showHeader
        showFooter={false}
        showMainContent={false}
        logoOnlyHeader
        showHelpLink
        helpLink={{ target: '_blank' }}
        // Drives the Help link's en/fr text + koodomobile.com href.
        headerProps={{ headerLocale }}
        showLocaleBadge
        localeOptions={localeOptions}
        onLanguageChange={handleLanguageChange}
        onProvinceChange={handleProvinceChange}
        closeLocaleDrawerOnSelect
        localeBadgeAriaLabel="Open language and region selector"
        logoNode={logoNode}
      />
    </div>
  )
}

export default SiteHeader
